import { createContext, useContext, useCallback, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useEffect } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface FeatureMeta {
  id: string;
  label: string;
  description: string;
  triggerType: 'dialog' | 'panel' | 'action' | 'editor';
  personas: Array<'seller' | 'buyer_seller' | 'buyer'>;
}

export interface RouteMeta {
  path: string;
  title: string;
  description: string;
  personas: Array<'seller' | 'buyer_seller' | 'buyer'>;
  concepts: string[];
}

// ── Store (external to React for zero re-renders) ──────────────────────────────

type Listener = () => void;

function createFeatureStore() {
  const features = new Map<string, FeatureMeta>();
  const listeners = new Set<Listener>();
  let snapshot: FeatureMeta[] = [];

  function emit() {
    snapshot = Array.from(features.values());
    listeners.forEach((l) => l());
  }

  return {
    register(meta: FeatureMeta) {
      features.set(meta.id, meta);
      emit();
    },
    unregister(id: string) {
      features.delete(id);
      emit();
    },
    getSnapshot(): FeatureMeta[] {
      return snapshot;
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// ── Context ────────────────────────────────────────────────────────────────────

interface FeatureRegistryContextValue {
  register: (meta: FeatureMeta) => void;
  unregister: (id: string) => void;
  subscribe: (listener: Listener) => () => void;
  getSnapshot: () => FeatureMeta[];
}

const FeatureRegistryContext = createContext<FeatureRegistryContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function FeatureRegistryProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<ReturnType<typeof createFeatureStore>>();
  if (!storeRef.current) {
    storeRef.current = createFeatureStore();
  }
  const store = storeRef.current;

  return (
    <FeatureRegistryContext.Provider value={store}>
      {children}
    </FeatureRegistryContext.Provider>
  );
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

/**
 * Register a feature on mount, unregister on unmount.
 * Completely inert if no FeatureRegistryProvider is present (graceful degradation).
 */
export function useFeatureRegistration(meta: FeatureMeta) {
  const ctx = useContext(FeatureRegistryContext);

  useEffect(() => {
    if (!ctx) return;
    ctx.register(meta);
    return () => ctx.unregister(meta.id);
    // meta is a static constant — only id matters for cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, meta.id]);
}

/**
 * Read currently registered features (reactive).
 * Returns empty array if provider is missing.
 */
export function useRegisteredFeatures(): FeatureMeta[] {
  const ctx = useContext(FeatureRegistryContext);

  const subscribe = useCallback(
    (cb: Listener) => (ctx ? ctx.subscribe(cb) : () => {}),
    [ctx],
  );
  const getSnapshot = useCallback(
    () => (ctx ? ctx.getSnapshot() : []),
    [ctx],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
